import { useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { Flipped, Flipper } from 'react-flip-toolkit';

import { Box, Stack, Typography } from '@mui/material';

import { SearchOffOutlined } from '@mui/icons-material';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';
import { GenericError } from '@/components/error/GenericError';
import { extractPaginationProps } from '@/utils/queries/queryExtracts';
import { nonFalsey } from '@/utils/nonFalsey';
import { InfiniteScroll } from '@/components/infinite-scroll/InfiniteScroll';

import { LoadingPosts } from '@/app/(main)/g/[machineName]/noticeboard/_components/LoadingPosts';

import { Or2Card } from '@/components/Surface/Or2Card';

import { Post } from './post-collection/Post';
import { IntroPost } from './IntroPost';

export const POST_LIMIT = 10;

export const POST_COLLECTION_QUERY = graphql(`
  query PostCollection(
    $machineName: String!
    $first: Int
    $cursor: String
    $tagIds: [ID]
  ) {
    group(machineName: $machineName) {
      _id
      machineName
      type {
        _id
        groupReferenceName
      }
      postsConnection(
        first: $first
        after: $cursor
        filterBy: { postTags: $tagIds }
        orderBy: CREATED_DESC
      ) @connection(key: "noticeboardposts") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...postPostFragment
          }
        }
      }
    }
  }
`);

const POST_COLLECTION_GROUP_FRAGMENT = graphql(`
  fragment postCollectionGroupFields on Group {
    ...postGroupFields
    _id
    machineName
  }
`);

interface Props {
  group: DocumentType<typeof POST_COLLECTION_GROUP_FRAGMENT>;
  topicIds: string[] | null;
  getSubgroupColor: (subgroupId: string) => string;
}

export const PostCollection = ({
  group: groupProp,
  topicIds,
  getSubgroupColor,
}: Props) => {
  const { machineName } = groupProp ?? {};

  const [fetchOutstanding, setFetchOutstanding] = useState(false);

  const { data, error, loading, fetchMore } = useQuery(POST_COLLECTION_QUERY, {
    variables: {
      machineName: machineName ?? '',
      tagIds: topicIds && topicIds.length ? topicIds : null,
      first: POST_LIMIT,
      cursor: null,
    },
    skip: !machineName,
    fetchPolicy: 'cache-and-network',
  });

  if (error) {
    return <GenericError />;
  }

  const { group } = data || {};
  const { postsConnection } = group ?? {};
  const groupReferenceName = group?.type?.groupReferenceName ?? 'group';

  const {
    nodes: posts,
    hasNextPage,
    endCursor,
  } = extractPaginationProps(postsConnection);

  const flipKey = posts
    .map((post) => post?._id)
    .filter(nonFalsey)
    .join(':');

  const fetchMorePosts = async () => {
    if (fetchOutstanding) {
      return;
    }

    setFetchOutstanding(true);

    await fetchMore({
      variables: {
        cursor: endCursor,
      },
    });

    setFetchOutstanding(false);
  };

  const loader = (
    <Box sx={{ paddingTop: 2 }}>
      <LoadingPosts />
    </Box>
  );

  // If there are no posts and we are filtering topics, show a message
  if (posts.length === 0 && !loading && topicIds && topicIds.length) {
    return (
      <Or2Card sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <SearchOffOutlined fontSize="large" color="action" />
          <Typography variant="body1" color="textSecondary">
            No posts found matching the topics selected
          </Typography>
        </Stack>
      </Or2Card>
    );
  }

  // If there are no posts and we're not filtering topics, show the intro post
  if (posts.length === 0 && !loading) {
    return <IntroPost groupReferenceName={groupReferenceName} />;
  }

  return (
    <div>
      <InfiniteScroll
        load={fetchMorePosts}
        hasMore={hasNextPage}
        isLoading={loading}
        loader={loader}
        initialLoader={<LoadingPosts />}
      >
        <Flipper flipKey={flipKey}>
          <Stack spacing={3}>
            {posts.map((post) => (
              <Flipped key={post?._id || undefined} flipId={post?._id ?? ''}>
                <Post
                  post={post}
                  group={groupProp}
                  getSubgroupColor={getSubgroupColor}
                />
              </Flipped>
            ))}
          </Stack>
        </Flipper>
      </InfiniteScroll>
    </div>
  );
};
